/**
 * CustomPlayWizard for Poker Reborn
 *
 * Base popup shell for the Custom Play wizard. Provides the shared chrome
 * (overlay, popup background, title, confirm/cancel buttons) with a pluggable
 * content slot for game-type-specific UI.
 *
 * Each game type provides its own WizardContent implementation:
 * - Cash: CashWizardContent (stakes + seat toggle)
 * - Fast-Fold: FastFoldWizardContent (stakes + "6-max only")
 * - SNG: SngWizardContent (buy-in tiers + "9-max only")
 *
 * The content is created via GameTypeDescriptor.createWizardContent().
 *
 * @see docs/screens/lobby/popups/CUSTOM_PLAY_WIZARD.md - Visual specs, dimensions, behavior
 * @see docs/screens/lobby/GAME_TYPE_DESCRIPTORS.md - Game type descriptor pattern
 * @see src/config/gameTypes.ts - Game type descriptors
 * @see src/components/lobby/wizard/WizardContent.ts - Content interface
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button } from '@/components/common/Button';
import type { ButtonStyle } from '@/components/common/Button';
import type { GameType, ManagerConfig } from '@/config/managers';
import { getGameTypeDescriptor } from '@/config/gameTypes';
import type { WizardContent } from './wizard/WizardContent';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  popup: {
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 16,
    width: 320,
    padding: 20,
  },

  title: {
    fill: 0xffffff,
    fontSize: 20,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Confirm button */
  confirm: {
    backgroundColor: 0x2d8a27,
    hoverColor: 0x3daa37,
    pressColor: 0x1d6a17,
    borderColor: 0x4aba44,
    textColor: 0xffffff,
    cornerRadius: 8,
    fontSize: 16,
  } satisfies Partial<ButtonStyle>,

  /** Cancel button */
  cancel: {
    backgroundColor: 0x333344,
    hoverColor: 0x444455,
    pressColor: 0x222233,
    borderColor: 0x555566,
    textColor: 0xaaaaaa,
    cornerRadius: 8,
    fontSize: 14,
  } satisfies Partial<ButtonStyle>,

  gaps: {
    titleToContent: 20,
    toConfirm: 20,
    confirmHeight: 42,
    cancelHeight: 36,
    confirmToCancelGap: 8,
  },
};

// =============================================================================
// CUSTOM PLAY WIZARD (BASE SHELL)
// =============================================================================

export class CustomPlayWizard extends Container {
  private overlay: Graphics;
  private popupContainer: Container;
  private popupBackground: Graphics;
  private confirmButton: Button;
  private content: WizardContent;

  private screenWidth: number;
  private screenHeight: number;

  /** Callback when a manager is selected and confirmed */
  onSelectManager?: (manager: ManagerConfig) => void;
  /** Callback when cancelled */
  onCancel?: () => void;

  constructor(gameType: GameType, screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    const descriptor = getGameTypeDescriptor(gameType);
    const contentWidth = STYLES.popup.width - STYLES.popup.padding * 2;

    // Overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', () => { this.handleCancel(); });
    this.addChild(this.overlay);

    // Popup container (centered)
    this.popupContainer = new Container();
    this.addChild(this.popupContainer);

    // Background
    this.popupBackground = new Graphics();
    this.popupBackground.eventMode = 'static';
    this.popupBackground.on('pointerdown', (e) => { e.stopPropagation(); });
    this.popupContainer.addChild(this.popupBackground);

    // Title
    const titleText = new Text({
      text: descriptor.title,
      style: STYLES.title,
    });
    titleText.x = STYLES.popup.padding;
    titleText.y = STYLES.popup.padding;
    this.popupContainer.addChild(titleText);

    // Content slot (game-type-specific)
    this.content = descriptor.createWizardContent(contentWidth);
    this.content.container.x = STYLES.popup.padding;
    this.content.container.y = STYLES.popup.padding + titleText.height + STYLES.gaps.titleToContent;
    this.popupContainer.addChild(this.content.container);

    // Wire readiness to confirm button state
    this.content.onReadinessChange((ready) => {
      this.confirmButton.disabled = !ready;
    });

    // Calculate Y position after content
    const afterContentY = this.content.container.y + this.content.getContentHeight();

    // Confirm button
    const buttonWidth = contentWidth;
    const confirmY = afterContentY + STYLES.gaps.toConfirm;

    this.confirmButton = new Button({
      label: 'JOIN TABLE',
      width: buttonWidth,
      height: STYLES.gaps.confirmHeight,
      style: STYLES.confirm,
    });
    this.confirmButton.x = STYLES.popup.padding;
    this.confirmButton.y = confirmY;
    this.confirmButton.disabled = true; // Disabled until content is ready
    this.confirmButton.onClick = () => { this.handleConfirm(); };
    this.popupContainer.addChild(this.confirmButton);

    // Cancel button
    const cancelButton = new Button({
      label: 'Cancel',
      width: buttonWidth,
      height: STYLES.gaps.cancelHeight,
      style: STYLES.cancel,
    });
    cancelButton.x = STYLES.popup.padding;
    cancelButton.y = confirmY + STYLES.gaps.confirmHeight + STYLES.gaps.confirmToCancelGap;
    cancelButton.onClick = () => { this.handleCancel(); };
    this.popupContainer.addChild(cancelButton);

    // Calculate total popup height
    const totalHeight = cancelButton.y + STYLES.gaps.cancelHeight + STYLES.popup.padding;

    // Draw background and center
    this.drawPopupBackground(totalHeight);
    this.centerPopup(totalHeight);
  }

  /** Resize the wizard overlay */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Re-center popup
    const totalHeight = this.popupContainer.height;
    this.centerPopup(totalHeight);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private handleConfirm(): void {
    const manager = this.content.getSelectedManager();

    if (manager) {
      this.onSelectManager?.(manager);
    }

    this.destroy();
  }

  private handleCancel(): void {
    this.onCancel?.();
    this.destroy();
  }

  private drawPopupBackground(height: number): void {
    const { fill, stroke, cornerRadius, width } = STYLES.popup;
    this.popupBackground.clear();
    this.popupBackground.roundRect(0, 0, width, height, cornerRadius);
    this.popupBackground.fill(fill);
    this.popupBackground.roundRect(0, 0, width, height, cornerRadius);
    this.popupBackground.stroke({ width: 2, color: stroke });
  }

  private centerPopup(height: number): void {
    this.popupContainer.x = (this.screenWidth - STYLES.popup.width) / 2;
    this.popupContainer.y = (this.screenHeight - height) / 2;
  }
}
